import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import { COLORS } from '../data/mockData';
import { fetchPaymentStatus } from '../services/api';

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'pending', txn: null, attempts: 0 });
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setErr('Missing checkout session ID');
      setState({ status: 'error', txn: null, attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      attempt += 1;
      try {
        const txn = await fetchPaymentStatus(sessionId);
        if (cancelled) return;
        if (txn.payment_status === 'paid' || txn.fulfilled) {
          setState({ status: 'paid', txn, attempts: attempt });
          return;
        }
        if (txn.status === 'expired' || txn.status === 'canceled') {
          setState({ status: 'failed', txn, attempts: attempt });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ status: 'timeout', txn, attempts: attempt });
          return;
        }
        setState({ status: 'pending', txn, attempts: attempt });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
        setState({ status: 'error', txn: null, attempts: attempt });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" style={{ maxWidth: 540, margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>
      {state.status === 'pending' && (
        <>
          <Loader2 size={48} color={COLORS.primary} style={{ animation: 'spin 1.2s linear infinite', margin: '0 auto' }} />
          <h1 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>Confirming your payment…</h1>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 6 }}>Attempt {state.attempts} of {MAX_POLLS}. This usually takes a few seconds.</p>
        </>
      )}
      {state.status === 'paid' && (
        <>
          <CheckCircle2 size={56} color={COLORS.success} style={{ margin: '0 auto' }} />
          <h1 data-testid="payment-success-title" style={{ fontSize: 24, fontWeight: 900, color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>Payment confirmed</h1>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, marginTop: 8, lineHeight: 1.5 }}>
            {state.txn?.kind === 'membership'
              ? `Your ${(state.txn?.metadata?.kind === 'membership' && state.txn?.metadata?.tier) ? 'tier ' + state.txn.metadata.tier : ''} membership is active. Refresh the app to see your new perks.`
              : 'Your order has been recorded. Look out for a receipt by email.'}
          </p>
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 8 }}>
            <button onClick={() => navigate('/dashboard')} style={{ padding: '12px 22px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              Go to Dashboard <ArrowRight size={16} />
            </button>
          </div>
        </>
      )}
      {(state.status === 'failed' || state.status === 'timeout' || state.status === 'error') && (
        <>
          <AlertTriangle size={48} color={COLORS.error} style={{ margin: '0 auto' }} />
          <h1 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, marginTop: 16, fontFamily: 'Outfit, sans-serif' }}>
            {state.status === 'failed' ? 'Payment was cancelled' : state.status === 'timeout' ? 'Payment confirmation timed out' : 'Something went wrong'}
          </h1>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 6 }}>
            {err || 'You can try again from the membership page. If you were charged, it will be reflected automatically — no action needed.'}
          </p>
          <Link to="/dashboard/membership" style={{ display: 'inline-block', marginTop: 24, padding: '12px 22px', borderRadius: 9999, backgroundColor: COLORS.primary, color: '#FFF', textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
            Back to Memberships
          </Link>
        </>
      )}
    </div>
  );
}
